/* istanbul ignore file -- @preserve app entry point; bootstrap/mount code runs only in the browser, not in Jest */
import { StrictMode, Suspense, lazy } from "react"
import ReactDOM from "react-dom/client"
import { Auth0Provider } from "@auth0/auth0-react"
import { QueryClientProvider } from "@tanstack/react-query"
import { HelmetProvider } from "react-helmet-async"
import App from "./App"
import reportWebVitals from "./reportWebVitals"
import queryClient from "./lib/queryClient"
import { setNewRelicEnvironment } from "./helpers/telemetry"
import "./assets/css/colorConstant.scss"
import "./assets/css/common.scss"
import "./assets/css/shared-components.scss" // Shared cards, KPI, detail layout
import "./globals.css"

/* istanbul ignore next -- demo-mode branch unreachable when NODE_ENV=test */
// Two separate concerns:
//   isDemoMode  -- demo flow with MSW-mocked backend (stakeholder demos)
//   isLocalMode -- cp-local-env: real cp-web-e-api at localhost:8080 via setupProxy
// Both share the role-picker plumbing (DemoAuthProvider + /demo/role-select),
// captured by useFakeAuth. MSW must NEVER start in local mode -- it would hijack
// the API calls that should reach the local Mule stack.
const isDemoMode =
  process.env.REACT_APP_DEMO_MODE === "true" &&
  process.env.NODE_ENV !== "test"
const isLocalMode =
  process.env.REACT_APP_AUTH_MODE === "local" &&
  process.env.NODE_ENV !== "test"
const useFakeAuth = isDemoMode || isLocalMode

// Lazy-load devtools so the ~200KB bundle is excluded from production builds
const ReactQueryDevtools =
  process.env.NODE_ENV === "development"
    ? lazy(() =>
        import("@tanstack/react-query-devtools").then((m) => ({
          default: m.ReactQueryDevtools,
        })),
      )
    : null

// Tag all RUM events with the deployment environment as early as possible.
setNewRelicEnvironment()

const root = ReactDOM.createRoot(document.getElementById("root"))
const callBack = `${window.location.protocol}//${window.location.host}/home`

const AUTH0_RETURN_TO_KEY = "auth0_returnTo"

const onRedirectCallback = (appState) => {
  if (appState?.returnTo) {
    try {
      window.sessionStorage.setItem(AUTH0_RETURN_TO_KEY, appState.returnTo)
    } catch {
      // sessionStorage may be unavailable in private browsing — non-critical
    }
  }
  // Strip OAuth callback params (?code=...&state=...) from the URL
  window.history.replaceState({}, document.title, window.location.pathname)
}

const renderApp = () => {
  /* istanbul ignore next -- demo-mode branch unreachable when NODE_ENV=test */
  const AuthProvider = useFakeAuth
    ? require("./demo/DemoAuthProvider").default
    : null

  /* istanbul ignore next -- demo-mode branch unreachable when NODE_ENV=test */
  const authWrapper = useFakeAuth ? (
    <AuthProvider>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </AuthProvider>
  ) : (
    <Auth0Provider
      domain={process.env.REACT_APP_Auth0Doman}
      clientId={process.env.REACT_APP_Auth0ClientId}
      authorizationParams={{
        redirect_uri: callBack,
        audience: process.env.REACT_APP_Auth0Audience,
        scope: process.env.REACT_APP_Auth0Scope,
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </Auth0Provider>
  )

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        {authWrapper}
        {process.env.NODE_ENV === "development" && ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </QueryClientProvider>
    </StrictMode>,
  )
}

const bootstrap = async () => {
  /* istanbul ignore next -- demo-mode branch unreachable when NODE_ENV=test */
  if (isDemoMode) {
    // MSW backend mocks: demo mode ONLY. In local mode the API calls go to
    // the real cp-web-e-api at localhost:8080 via setupProxy.
    try {
      const { startDemoWorker } = await import("./demo")
      await startDemoWorker()
    } catch (err) {
      console.warn("[Demo] Worker bootstrap failed:", err)
    }
  }

  /* istanbul ignore next -- fake-auth branch unreachable when NODE_ENV=test */
  if (useFakeAuth) {
    // Demo OR local: role-picker redirect when no role is stored yet.
    const storedRole = sessionStorage.getItem("demo-selected-role")
    if (
      !storedRole &&
      window.location.pathname !== "/" &&
      window.location.pathname !== "/demo/role-select"
    ) {
      window.location.href = "/demo/role-select"
      return
    }
  }

  renderApp()
}

void bootstrap()

reportWebVitals()

// ── Chunk-load error recovery ───────────────────────────────────────────
// After a deploy, browsers with a cached index.html may request old chunk
// hashes that no longer exist on the CDN → ChunkLoadError. Detect this and
// do a single hard reload so the browser fetches the new index.html + chunks.
window.addEventListener("error", (e) => {
  if (
    e.message?.includes("ChunkLoadError") ||
    e.message?.includes("Loading chunk") ||
    e.message?.includes("Loading CSS chunk")
  ) {
    const reloaded = sessionStorage.getItem("chunk_reload")
    if (!reloaded) {
      sessionStorage.setItem("chunk_reload", "1")
      window.location.reload()
    }
  }
})
// Clear the flag on successful load so future deploys can trigger it again
sessionStorage.removeItem("chunk_reload")

